import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Artwork } from '../dashboard/Artwork';
import { Button, LinkButton } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Explicit } from '../../components/ui/Explicit';
import { LoadingPanel } from '../../components/ui/Loader';
import { useDocumentTitle } from '../../lib/title';
import { useSession } from '../../stores/session';
import { SubmitDialog } from './SubmitDialog';
import { TIER_TONE, startsIn } from './api';
import { useRoom } from './useRoom';

/**
 * The running order, at an address a host can paste anywhere.
 *
 * ## Why it is a page and not the strip
 *
 * The queue already appears in two places, and neither is shareable. The live
 * room's strip is eight cards wide and lives under a broadcast nobody arriving
 * from a Twitch chat has loaded yet; the console is the host's own. A host
 * saying "get in the queue" in a stream needs one link that opens on a phone
 * and answers the two questions somebody has - what is coming, and how long
 * until mine - without them having to find the room first.
 *
 * ## The estimate is the server's, not a guess made here
 *
 * `starts_in_ms` is a running total of everything ahead of a row, measured from
 * the end of what is currently playing - see `StationPresenter::queue`. It is
 * null while the room is paused, deliberately: a countdown that keeps counting
 * while nothing is playing tells everybody they are closer than they are.
 *
 * ## Nothing here decides who may see it
 *
 * `show_queue` is enforced in `ShowStationAction`, which returns an empty queue
 * to anybody but the host when it is off. So a hidden queue arrives here as an
 * empty one and this page says so, rather than this page knowing a rule the
 * server also knows.
 */
export function QueuePage() {
  const { username = '' } = useParams();
  const me = useSession((state) => state.user);
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data, isPending } = useRoom(username);

  useDocumentTitle(data?.room ? `Queue — ${data.room.name ?? data.room.host.display_name}` : 'Queue');

  if (isPending) return <LoadingPanel />;

  if (!data?.room) {
    return (
      <Shell>
        <Card className="p-6">
          <p className="text-sm text-soft">Nobody here goes by @{username}.</p>
        </Card>
      </Shell>
    );
  }

  const { queue } = data;
  /*
   * Narrowed once. `RoomView.room` is nullable because a profile asks this for
   * everybody it renders and most people have never hosted - but the guard
   * above has already dealt with that, and TypeScript cannot see through a
   * destructure.
   */
  const room = data.room;
  const live = room.status === 'live';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/${room.username}/queue`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the address bar still has the URL.
      setCopied(false);
    }
  };

  return (
    <Shell>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight">
            {room.name ?? `${room.host.display_name}'s queue`}
          </h1>
          <p className="mt-1 text-sm text-soft">
            {live ? 'On air now' : 'Not on air'} ·{' '}
            <Link to={`/${room.username}`} className="hover:underline">
              @{room.username}
            </Link>
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {/*
            The whole reason this page exists, so it is a button and not a
            thing you select out of the address bar.
          */}
          <Button variant="secondary" data-copy-queue-link onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy link'}
          </Button>

          {me ? (
            <Button data-queue-submit onClick={() => setSubmitting(true)}>
              Submit a track
            </Button>
          ) : (
            <LinkButton to="/login">Sign in to submit</LinkButton>
          )}
        </div>
      </header>

      {/*
        Now playing, because "how long until mine" is measured from the end of
        it and the answer is meaningless without saying what it is.
      */}
      {live && room.now_playing && (
        <Card className="flex items-center gap-4 p-4" data-queue-now>
          <Artwork
            track={{
              title: room.now_playing.title,
              artwork: null,
              artwork_small: room.now_playing.artwork_small,
            }}
            className="h-12 w-12 shrink-0 rounded-[var(--radius-chip)]"
          />
          <div className="min-w-0">
            <span className="meta-caps text-faint">Now playing</span>
            <p className="truncate text-sm font-medium text-ink">{room.now_playing.title}</p>
          </div>
        </Card>
      )}

      {queue.length === 0 ? (
        <Card className="p-6" data-queue-empty>
          <p className="text-sm text-soft">
            {room.show_queue === false
              ? `${room.host.display_name} keeps their running order private. You can still submit — you just will not see what is ahead of you.`
              : 'Nothing queued yet. A submission now goes on first.'}
          </p>
        </Card>
      ) : (
        <ol className="flex flex-col gap-2" data-queue-list>
          {queue.map((item) => {
            const mine = item.submitter?.username === me?.username;

            return (
              <li key={item.id}>
                <Card
                  className={`flex items-center gap-4 p-3 ${mine ? 'ring-1 ring-accent' : ''}`}
                  data-queue-item={item.id}
                  data-queue-mine={mine ? 'true' : undefined}
                >
                  <span className="w-6 shrink-0 text-center font-mono text-[11px] text-faint">
                    {item.position}
                  </span>

                  <Artwork
                    track={{
                      title: item.track.title,
                      artwork: null,
                      artwork_small: item.track.artwork_small,
                    }}
                    className="h-11 w-11 shrink-0 rounded-[var(--radius-chip)]"
                  />

                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/tracks/${item.track.id}`}
                      className="block truncate text-sm font-medium text-ink hover:underline"
                    >
                      {item.track.explicit && <Explicit className="mr-1 align-[-2px]" />}
                      {item.track.title}
                    </Link>
                    <Link
                      to={`/${item.artist.username}`}
                      className="block truncate font-mono text-[11px] text-soft hover:underline"
                    >
                      {item.artist.display_name}
                    </Link>
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-0.5 text-right">
                    {/*
                      The answer somebody came for. Larger than the tier beside
                      it, because "when" is the question and "how it got there"
                      is the footnote.
                    */}
                    <span className="font-mono text-[13px] text-ink" data-queue-eta={item.id}>
                      {live
                        ? item.starts_in_ms === null
                          ? 'paused'
                          : startsIn(item.starts_in_ms)
                        : 'when they go on air'}
                    </span>
                    <span className={`font-mono text-[10px] ${TIER_TONE[item.boost_tier]}`}>
                      {item.boost_tier === 'pick' ? 'host pick' : item.boost_tier}
                      {item.auto_filled && ' · auto'}
                      {mine && ' · yours'}
                    </span>
                  </div>
                </Card>
              </li>
            );
          })}
        </ol>
      )}

      {submitting && me && (
        <SubmitDialog
          room={room}
          username={me.username}
          onClose={() => setSubmitting(false)}
          onSubmitted={() => {
            setSubmitting(false);
            void queryClient.invalidateQueries({ queryKey: ['room', room.username] });
          }}
        />
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-5 px-6 py-10" data-queue-page>
      {children}
    </div>
  );
}
